import {
  DataTable,
  Icon,
  FormattedRelativeTime,
  FormattedDateTime,
  Box,
  Button,
  ButtonGroup,
  DataTableHeader,
  DataTableHeaderActions,
  DataTableHeaderActionsRightSection,
  DataTableHeaderActionsLeftSection,
  DataTableHeaderSavedViews,
  DataTableTable,
  DataTableHeaderRow,
  Panel,
  PanelInset,
  PanelSection,
  EmptyState,
  EmptyStateTitle,
  EmptyStateDescription,
  InlineStack,
  Toggle,
  Paragraph,
  useConfirmationModal,
  DataTableHeaderFilters,
  useSnackbars,
  Text,
  Strong,
} from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import type { Row } from '@tanstack/table-core';
import { createColumnHelper } from '@tanstack/table-core';
import { useQueryState, parseAsStringLiteral, parseAsBoolean } from 'nuqs';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import type { BackfillComputed } from '@/features/tasks';
import { useTasks, TaskStateBadge } from '@/features/tasks';
import { TaskObject } from '../task-object';
import { TaskExpandedRow } from '../task-expanded-row';
import { isEmptyString } from '@/utils/string';
import { type BackfillState, backfillStates } from '@/services/api/services-api';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import {
  BACKFILL_TYPES,
  type BackfillType,
  getDeprioritizeTooltip,
  isInvisibleLabel,
} from '@/features/tasks/utils/task-types';
import { getTaskDisplayParts } from '@/features/tasks/utils/task-display';
import { useMemo } from 'react';
import { STATE_LABELS } from '@/features/tasks/components/task-state-badge';
import { queryClient } from '@/layouts/root';
import { servicesQuery } from '@/services/api';
import { WORKSPACE_HEALTH_REFETCH_INTERVAL } from '@/features/workspace-health';

const columnHelper = createColumnHelper<BackfillComputed>();

const columns = [
  columnHelper.display({
    id: 'icon',
    header: '',
    cell: (info) => {
      const backfill = info.row.original;
      return (
        <Box display="flex" alignItems="center" h="full">
          <Icon name={backfill.info.icon as IconName} />
        </Box>
      );
    },
    meta: {
      width: 44,
    },
  }),
  columnHelper.accessor('info.label', {
    id: 'task',
    header: 'Task',
    cell: (info) => info.getValue(),
    meta: {
      width: '1fr',
    },
  }),
  columnHelper.display({
    id: 'detail',
    header: 'Detail',
    cell: (info) => {
      const backfill = info.row.original;
      return <TaskObject backfill={backfill} />;
    },
    meta: {
      width: '2fr',
    },
  }),
  columnHelper.display({
    id: 'updated',
    header: 'Updated',
    cell: (info) => {
      const backfill = info.row.original;
      const lastUpdate = backfill.completed ?? backfill.started ?? backfill.created;

      return <FormattedRelativeTime date={new Date(lastUpdate * 1000)} size="s" color="subtle" />;
    },
    meta: {
      align: 'end',
    },
  }),
  columnHelper.accessor('state', {
    id: 'status',
    header: 'Status',
    cell: (info) => <TaskStateBadge state={info.getValue()} />,
    meta: {
      align: 'end',
    },
  }),
];

const emptyStateMessages = {
  Success: 'No successful tasks',
  Failure: 'No failed tasks',
  InProgress: 'No in-progress tasks',
  Queued: 'No queued tasks',
  Retrying: 'No retrying tasks',
  Canceled: 'No canceled tasks',
} as const;

export function TasksTable({
  isWorkspaceHealthView = false,
  isDataTabActive = false,
}: {
  isWorkspaceHealthView?: boolean;
  isDataTabActive?: boolean;
}) {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const [continuation, setContinuation] = useQueryState('continuation');
  const [expandedId, setExpandedId] = useQueryState('expand');
  const [backfillState, setBackfillState] = useQueryState('state', parseAsStringLiteral<BackfillState>(backfillStates));
  const [backfillType, setBackfillType] = useQueryState(
    'type',
    parseAsStringLiteral<BackfillType>(Object.keys(BACKFILL_TYPES) as readonly BackfillType[]),
  );

  const isPerformanceDashboardEnabled = useFeatureFlag(AccountFeatureFlag.TasksPerformanceDashboard);

  const [canRefresh, setCanRefresh] = useQueryState(
    'refresh',
    parseAsBoolean.withDefault(isWorkspaceHealthView === true),
  );

  const queryEnabled = isWorkspaceHealthView === true ? isDataTabActive === true : true;

  const { tasks, meta, isLoading, dataUpdatedAt } = useTasks({
    workspaceId,
    state: backfillState,
    continuation,
    type: backfillType,
    enabled: queryEnabled,
    refetchInterval:
      canRefresh === true ? (isWorkspaceHealthView === true ? WORKSPACE_HEALTH_REFETCH_INTERVAL : 10_000) : undefined,
  });

  const lastUpdated = useMemo(() => (dataUpdatedAt > 0 ? new Date(dataUpdatedAt) : new Date()), [dataUpdatedAt]);

  const nextContinuation = meta?.continuation ?? '';

  const canAdvance = isEmptyString(nextContinuation) === false;
  const canRestart = isEmptyString(continuation) === false;

  const handleAdvance = () => {
    if (canAdvance === true) {
      void setContinuation(nextContinuation);
    }
  };

  const handleRestart = () => {
    if (canRestart === true) {
      void setContinuation(null);
    }
  };

  const handleAutoRefreshToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    void setCanRefresh(event.target.checked);
  };

  const savedViews = useMemo(() => {
    return {
      defaultViewName: 'All Tasks',
      views: Object.entries(emptyStateMessages).map(([key]) => ({
        id: key,
        name: STATE_LABELS[key as keyof typeof emptyStateMessages],
      })),
      activeViewId: backfillState,
      onActiveViewChange: (viewId: string | null) => {
        void setBackfillState(viewId as BackfillState);
        void setExpandedId(null);
        void setContinuation(null);
        void setBackfillType(null);
      },
    };
  }, [backfillState, setBackfillState, setExpandedId, setContinuation, setBackfillType]);

  const filtersConfig = useMemo(() => {
    return {
      filters: [
        {
          key: 'type',
          label: 'Task',
          type: 'radio' as const,
          options: Object.entries(BACKFILL_TYPES)
            .filter(([key]) => isInvisibleLabel(key as BackfillType) === false)
            .map(([key, type]) => ({ label: type.label, value: key }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        },
      ],
      values: backfillType != null ? [{ key: 'type', value: backfillType }] : [],
      onChange: (key: string, value: string) => {
        if (key === 'type') {
          void setBackfillType(value as BackfillType);
          void setContinuation(null);
        }
      },
      onClear: (key: string) => {
        if (key === 'type') {
          void setBackfillType(null);
          void setContinuation(null);
        }
      },
      onClearAll: () => {
        void setContinuation(null);
        void setBackfillType(null);
      },
    };
  }, [backfillType, setBackfillType, setContinuation]);

  const confirm = useConfirmationModal();
  const snackbars = useSnackbars();

  const deprioritizeBackfillsMutation = servicesQuery.deprioritizeBackfills.useMutation({
    onSuccess: (_, variables) => {
      const mode = variables?.body?.mode;
      const foundBackfill = tasks.find((bf) => bf.id === variables?.params?.backfill_id);
      const { displayText, objectTypeDisplay } = getTaskDisplayParts(foundBackfill);

      snackbars.create({
        message: `${objectTypeDisplay} ${displayText} ${
          mode === 'next' ? 'prioritized' : 'deprioritized'
        } successfully. Task priority will adjust accordingly.`.trim(),
        variant: 'success',
      });
    },
    onSettled: () => {
      // Slight delay to get the correct priority after the mutation is settled
      // 5 seconds is also the default interval for the snackbar to be closed, so it aligns nicely
      setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: ['backfills'],
        });
      }, 5_000);
    },
    onError: (_error, variables) => {
      const foundBackfill = tasks.find((bf) => bf.id === variables?.params?.backfill_id);
      const { taskDisplay } = getTaskDisplayParts(foundBackfill);

      snackbars.create({
        message: `Failed to ${
          variables?.body?.mode === 'next' ? 'prioritize' : 'deprioritize'
        } ${taskDisplay}. Please try again later!`,
        variant: 'error',
      });
    },
  });

  const handleDeprioritize = (task: BackfillComputed, mode: 'next' | 'last') => {
    const modeLabel = mode === 'next' ? 'be next in the queue' : 'be at the end of the queue';
    const action = mode === 'next' ? 'Prioritize' : 'Deprioritize';
    const { taskDisplay: taskDisplayName } = getTaskDisplayParts(task, {
      displayTextFallback: 'task',
      objectTypeFallback: 'this',
    });

    confirm({
      title: `${action} ${taskDisplayName}`,
      message: (
        <Text>
          Are you sure you want to move <Strong>{taskDisplayName}</Strong> to {modeLabel}? This action cannot be undone.
        </Text>
      ),
      isDanger: true,
      onConfirm: () => {
        void deprioritizeBackfillsMutation.mutate({
          params: { environment_id: workspaceId ?? 0, backfill_id: task.id },
          body: { mode },
        });
      },
    });
  };

  const rowActions = (row: Row<BackfillComputed>) => {
    const isDisabled = ['Success', 'Failure', 'Canceled'].includes(row.original.state);
    return {
      dropdownActions: [
        {
          label: row.original.state === 'InProgress' ? 'Stop & move to next in queue' : 'Move to next in queue',
          variant: 'secondary' as const,
          onClick: () => {
            handleDeprioritize(row.original, 'next');
          },
          tooltip: getDeprioritizeTooltip(row.original.state, 'next'),
          isDanger: true,
          icon: 'decreasing',
          isDisabled,
        },
        {
          label: row.original.state === 'InProgress' ? 'Stop & move to end of queue' : 'Move to end of queue',
          variant: 'secondary' as const,
          onClick: () => {
            handleDeprioritize(row.original, 'last');
          },
          tooltip: getDeprioritizeTooltip(row.original.state, 'last'),
          isDanger: true,
          icon: 'chevron-down-double',
          isDisabled,
        },
      ],
    };
  };

  return (
    <DataTable
      caption="Background tasks"
      data={tasks}
      columns={columns}
      withRowExpanding={true}
      expandedRowComponent={TaskExpandedRow}
      initiallyExpandedRows={isEmptyString(expandedId) === false ? { [expandedId]: true } : {}}
      getRowId={(row: BackfillComputed) => String(row.id)}
      isLoading={isLoading}
      withSavedViews={savedViews != null}
      savedViews={savedViews}
      withFilters={filtersConfig != null}
      filters={filtersConfig}
      rowActions={isPerformanceDashboardEnabled === true ? rowActions : undefined}
    >
      <Panel
        style={
          isWorkspaceHealthView === true
            ? {
                borderRadius: 0,
                borderLeftColor: 'transparent',
                borderRightColor: 'transparent',
                borderBottomColor: 'transparent',
              }
            : undefined
        }
      >
        <DataTableHeader>
          {(header) => (
            <>
              {header.shouldShowSavedViews === true ? (
                <DataTableHeaderRow>
                  <DataTableHeaderSavedViews />
                </DataTableHeaderRow>
              ) : null}
              {header.shouldShowActions === true ? (
                <DataTableHeaderRow>
                  <DataTableHeaderActions>
                    <DataTableHeaderActionsLeftSection />
                    <DataTableHeaderActionsRightSection>
                      <InlineStack alignBlock="center" gap={isWorkspaceHealthView === true ? '100' : '200'}>
                        {isWorkspaceHealthView === true ? null : (
                          <>
                            <Paragraph variant="secondary">
                              Last updated{' '}
                              <FormattedDateTime
                                withRelativeDateFromNow={true}
                                format="full_with_time"
                                date={lastUpdated}
                              />
                            </Paragraph>
                            <Toggle
                              label="Auto-refresh"
                              isChecked={canRefresh === true}
                              onChange={handleAutoRefreshToggle}
                              labelOn="On"
                              labelOff="Off"
                              size="small"
                            />
                          </>
                        )}
                        <ButtonGroup variant="tertiary" size="small">
                          <Button
                            icon="chevron-left"
                            isDisabled={!canRestart}
                            onClick={handleRestart}
                            isIconOnly={true}
                          />
                          <Button
                            icon="chevron-right"
                            isDisabled={!canAdvance}
                            onClick={handleAdvance}
                            isIconOnly={true}
                          />
                        </ButtonGroup>
                      </InlineStack>
                    </DataTableHeaderActionsRightSection>
                  </DataTableHeaderActions>
                </DataTableHeaderRow>
              ) : null}
              {header.shouldShowFilters === true ? (
                <DataTableHeaderRow>
                  <DataTableHeaderFilters />
                </DataTableHeaderRow>
              ) : null}
            </>
          )}
        </DataTableHeader>
        {tasks.length === 0 && isLoading === false ? (
          <PanelSection>
            <EmptyState>
              <EmptyStateTitle icon="notifications">
                {backfillState != null ? emptyStateMessages[backfillState] : 'No Tasks'}
              </EmptyStateTitle>
              {backfillState == null ? (
                <EmptyStateDescription>Try sending a newsletter or building a segment!</EmptyStateDescription>
              ) : null}
            </EmptyState>
          </PanelSection>
        ) : (
          <PanelInset>
            <DataTableTable />
          </PanelInset>
        )}
      </Panel>
    </DataTable>
  );
}
